import { Search, ArrowRight } from 'lucide-react';
import { useRouter } from 'next/router';
import { useState, useRef, useEffect, Fragment } from 'react';
import { useSearchBox, useHits } from 'react-instantsearch';
import { SearchSku } from '@/types/searchHit';
import HeaderHit from '@/components/header/HeaderHit';
import HeaderEmptyHits from '@/components/header/HeaderEmptyHits';
import { Transition } from '@headlessui/react';

interface SellSearchProps {
  placeholder: string;
  onSkuSelect?: (sku: SearchSku) => void;
  disableSubmitNavigation?: boolean;
  variant?: 'default' | 'subtle';
}

export default function SellSearch({
  placeholder,
  onSkuSelect,
  disableSubmitNavigation = false,
  variant = 'default',
}: SellSearchProps) {
  const isSubtle = variant === 'subtle';
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOnFocus, setSearchOnFocus] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { refine } = useSearchBox();
  const { items } = useHits<SearchSku>();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchQuery.trim() || disableSubmitNavigation) {
      return;
    }

    router.push(`/search?query=${encodeURIComponent(searchQuery)}`);
  };

  const handleSkuSelect = (sku: SearchSku) => {
    if (!onSkuSelect) {
      return;
    }

    onSkuSelect(sku);
    setSearchQuery('');
    refine('');
    setSearchOnFocus(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSearchOnFocus(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`tw-relative tw-w-full ${isSubtle ? 'tw-mb-4' : 'tw-mb-6'}`} ref={containerRef}>
      <form onSubmit={handleSearch} className="tw-w-full">
        <div
          className={`tw-relative tw-flex tw-items-center tw-rounded-full tw-border tw-transition-all ${
            isSubtle
              ? 'tw-border-gray-300 tw-bg-gray-50 tw-px-5 tw-py-3 tw-shadow-sm focus-within:tw-border-main focus-within:tw-ring-1 focus-within:tw-ring-main/20'
              : 'tw-border-gray-200 tw-bg-white tw-px-6 tw-py-4 tw-shadow-fancyBoxShadow focus-within:tw-border-main focus-within:tw-ring-2 focus-within:tw-ring-main/20'
          }`}
        >
          <Search
            className={`tw-flex-shrink-0 tw-text-gray-400 ${isSubtle ? 'tw-h-5 tw-w-5' : 'tw-h-6 tw-w-6'}`}
          />
          <input
            type="text"
            placeholder={placeholder}
            className={`tw-w-full !tw-border-none tw-bg-transparent tw-text-text tw-outline-none ${
              isSubtle
                ? 'tw-pl-3 tw-text-base tw-placeholder-gray-500'
                : 'tw-pl-4 tw-text-lg tw-placeholder-gray-400'
            }`}
            value={searchQuery}
            onFocus={() => setSearchOnFocus(true)}
            onChange={e => {
              setSearchQuery(e.target.value);
              refine(e.target.value);
              setSearchOnFocus(true);
            }}
          />
          <button
            type="submit"
            className={`tw-absolute tw-top-1/2 tw-flex tw--translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-transition-colors ${
              isSubtle
                ? 'tw-right-1.5 tw-h-10 tw-w-10 tw-border tw-border-gray-200 tw-bg-white tw-text-main hover:tw-bg-gray-100'
                : 'tw-right-2 tw-h-12 tw-w-12 tw-border-2 tw-border-none tw-bg-main tw-text-white hover:tw-bg-blue-700'
            }`}
          >
            <ArrowRight className="tw-h-5 tw-w-5" />
          </button>
        </div>
      </form>

      {/* Search hits dropdown */}
      <Transition
        show={searchOnFocus && searchQuery?.trim().length > 0}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-translate-y-1"
        enterTo="tw-opacity-100 tw-translate-y-0"
        leave="tw-transition tw-ease-in tw-duration-150"
        leaveFrom="tw-opacity-100 tw-translate-y-0"
        leaveTo="tw-opacity-0 tw-translate-y-1"
        as={Fragment}
      >
        <div
          className={`tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-2 tw-w-full tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-text-left tw-ring-1 ${
            isSubtle
              ? 'tw-shadow-[0_6px_20px_rgb(0,0,0,0.08)] tw-ring-black/10'
              : 'tw-shadow-[0_8px_30px_rgb(0,0,0,0.12)] tw-ring-black/5'
          }`}
        >
          {items.length === 0 ? (
            <HeaderEmptyHits searchOnFocus={searchOnFocus} searchValue={searchQuery} />
          ) : (
            <ul className="tw-max-h-[400px] tw-list-none tw-divide-y tw-divide-gray-100 tw-overflow-y-auto tw-py-2 tw-pl-0">
              {[...items]
                .sort((a, b) => a.__position - b.__position)
                .map(item => (
                  <HeaderHit
                    key={item.objectID}
                    item={item}
                    onSelect={onSkuSelect ? handleSkuSelect : undefined}
                  />
                ))}
            </ul>
          )}
        </div>
      </Transition>
    </div>
  );
}
